import { useEffect, useState } from 'react';
import axios from 'axios';
import { API, useAuth } from '../context/AuthContext';
import { useFeatureFlags } from '../context/FeatureFlagContext';

// Who keys which pre-processing phase, and whether they key it blind (flag
// ppPhaseEntry). The server's answer (/pp-phases/meta) — the screen never works
// it out from the role itself. Cached per login for the life of the tab, because
// the tab strip, the sidebar guard and the phase pages all ask on every render.
let cache = { userId: null, data: null, inflight: null };

export function usePpPhaseMeta() {
  const { user } = useAuth();
  const { isEnabled } = useFeatureFlags();
  const on = isEnabled('ppPhaseEntry');
  const uid = user?.id || null;
  const [meta, setMeta] = useState(cache.userId === uid ? cache.data : null);

  useEffect(() => {
    if (!on || !uid) return;
    if (cache.userId !== uid) cache = { userId: uid, data: null, inflight: null };
    if (cache.data) { setMeta(cache.data); return; }
    if (!cache.inflight) {
      cache.inflight = axios.get(`${API}/pp-phases/meta`)
        .then((r) => { cache.data = r.data; return r.data; })
        // Refused (no pre-processing stage at all) or failed: not a phase user. Waiting
        // on an answer that is never coming would leave every gated page blank.
        .catch(() => { cache.data = { blind: false, manager: false, phases: [], stages: [], unavailable: true }; return cache.data; })
        .finally(() => { cache.inflight = null; });
    }
    let alive = true;
    cache.inflight?.then((d) => { if (alive) setMeta(d); }).catch(() => {});
    return () => { alive = false; };
  }, [on, uid]);

  return { on, meta: on ? meta : null, loading: on && !!uid && !meta };
}

// The plant's entry mode or a person's phases changed — drop the cached answer and
// reload, so the sidebar and every gated page are rebuilt from the new one.
export function resetPpPhaseMeta({ reload = true } = {}) {
  cache = { userId: null, data: null, inflight: null };
  if (reload) window.location.reload();
}

export const PHASE_LABELS = { arrival: 'Arrival', deheading: 'Deheading', grading: 'Grading' };

// A station saved, or Lot Check decided on a lot — the sidebar's pending-lot
// badges re-read their counts.
export const PP_PHASES_CHANGED = 'ppPhasesChanged';
export const announcePpPhasesChanged = () => {
  try { window.dispatchEvent(new Event(PP_PHASES_CHANGED)); } catch (e) { /* no window */ }
};
